import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTenantContext } from '../../context/TenantContext';
import { useToast } from '../../hooks/useToast';
import { InstructorLayout } from '../../components/instructor/InstructorLayout';
import { useInstructorPermissions } from '../../hooks/useInstructorPermissions';
import { getAvailableCourses } from '../../services/elearningCourseService';
import InstructorAnalyticsService from '../../services/instructorAnalyticsService';
import { 
  BookOpen, 
  Search, 
  Plus, 
  MoreHorizontal, 
  Edit, 
  Eye, 
  Trash, 
  Users,
  Star,
  Clock,
  TrendingUp,
  Filter,
  SortAsc,
  Play,
  Pause,
  Settings,
  GraduationCap,
  Calendar,
  DollarSign,
  Crown,
  Sparkles,
  Diamond,
  Gem
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { motion } from 'framer-motion';

interface Course {
  id: string;
  title: string;
  slug: string;
  description: string;
  thumbnail_url: string | null;
  status: 'draft' | 'published' | 'archived';
  difficulty_level: 'beginner' | 'intermediate' | 'advanced';
  duration_hours: number;
  price: number;
  enrollments_count: number;
  average_rating: number;
  reviews_count: number;
  created_at: string;
  updated_at: string;
  last_published_at: string | null;
}

const InstructorCoursesPage: React.FC = () => {
  const { tenant } = useTenantContext();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { instructor, hasPermission, isLoading: authLoading } = useInstructorPermissions(['view_courses']);
  
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('updated_at');
  const [totalUniqueStudents, setTotalUniqueStudents] = useState(0);

  // Libellés affichés dans les contrôles (FR)
  const STATUS_LABELS: Record<string, string> = {
    all: 'Tous les statuts',
    draft: 'Brouillon',
    published: 'Publié',
    archived: 'Archivé',
  };

  const SORT_LABELS: Record<string, string> = {
    updated_at: 'Dernière modification',
    created_at: 'Date de création',
    title: 'Titre',
    enrollments_count: "Nombre d'inscriptions",
    average_rating: 'Note moyenne',
    price: 'Prix',
  };

  useEffect(() => {
    if (tenant && instructor) {
      loadCourses();
    }
  }, [tenant, instructor]);

  const loadCourses = async () => {
    try {
      setLoading(true);
      
      if (!tenant?.id || !instructor?.id) {
        setLoading(false);
        return;
      }

      // Charger les cours de l'instructeur
      const coursesData = await getAvailableCourses({
        tenantId: tenant.id,
        instructorId: instructor.id,
        sortBy: sortBy as any,
        sortOrder: 'desc',
        limit: 50
      });
      
      // Charger le nombre d'étudiants uniques (comme dans le dashboard)
      const analytics = await InstructorAnalyticsService.getAnalyticsOverview(
        tenant.id,
        instructor.id,
        '1y' // Période plus large (supportée par le service) pour avoir tous les étudiants
      );
      setTotalUniqueStudents(analytics.totalStudents);
      
      // Filtrer par statut si nécessaire
      let filteredCourses = coursesData.courses;
      if (statusFilter !== 'all') {
        filteredCourses = filteredCourses.filter((course) => course.status === statusFilter);
      }
      
      // Filtrer par terme de recherche
      if (searchTerm) {
        filteredCourses = filteredCourses.filter((course) => 
          course.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          course.description?.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }
      
      setCourses(filteredCourses.map((course) => ({
        id: course.id,
        title: course.title,
        slug: course.slug,
        description: course.description || '',
        thumbnail_url: course.thumbnail_url,
        status: course.status as 'draft' | 'published' | 'archived',
        difficulty_level: course.difficulty_level,
        duration_hours: course.duration_hours,
        price: course.price,
        enrollments_count: course.enrollment_count,
        average_rating: course.rating_average,
        reviews_count: course.rating_count,
        created_at: course.created_at,
        updated_at: course.updated_at,
        last_published_at: course.status === 'published' ? course.updated_at : null
      })));
      
    } catch (error) {
      console.error('Erreur lors du chargement des cours:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de charger les cours',
        variant: 'destructive'
      });
      setCourses([]);
    } finally {
      setLoading(false);
    }
  };

  // Filtrer les cours localement pour éviter des appels API fréquents
  const filteredCourses = courses.filter(course => {
    const matchesSearch = course.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         course.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || course.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const sortedCourses = [...filteredCourses].sort((a, b) => {
    switch (sortBy) {
      case 'title':
        return a.title.localeCompare(b.title);
      case 'enrollments_count':
      case 'enrollments':
        return b.enrollments_count - a.enrollments_count;
      case 'average_rating':
      case 'rating':
        return b.average_rating - a.average_rating;
      case 'price':
        return b.price - a.price;
      case 'created_at':
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      case 'updated_at':
      default:
        return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
    }
  });

  const handleDeleteCourse = async (courseId: string) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer ce cours ?')) return;
    
    try {
      // TODO: Implémenter la suppression
      setCourses(courses.filter(course => course.id !== courseId));
      toast({
        title: 'Succès',
        description: 'Cours supprimé avec succès'
      });
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      toast({
        title: 'Erreur',
        description: 'Erreur lors de la suppression du cours',
        variant: 'destructive'
      });
    }
  };

  const handleToggleStatus = async (courseId: string, newStatus: 'published' | 'draft') => {
    try {
      // TODO: Implémenter le changement de statut
      setCourses(courses.map(course => 
        course.id === courseId 
          ? { ...course, status: newStatus, last_published_at: newStatus === 'published' ? new Date().toISOString() : course.last_published_at }
          : course
      ));
      toast({
        title: 'Succès',
        description: `Cours ${newStatus === 'published' ? 'publié' : 'mis en brouillon'} avec succès`
      });
    } catch (error) {
      console.error('Erreur lors du changement de statut:', error);
      toast({
        title: 'Erreur',
        description: 'Erreur lors du changement de statut',
        variant: 'destructive'
      });
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'published':
        return <Badge variant="outline" className="border-green-700 text-green-300 bg-green-600">Publié</Badge>;
      case 'draft':
        return <Badge variant="outline" className="border-amber-700 text-amber-300 bg-amber-600">Brouillon</Badge>;
      case 'archived':
        return <Badge variant="outline" className="border-gray-600 text-gray-300 bg-gray-600">Archivé</Badge>;
      default:
        return <Badge variant="outline" className="border-gray-700 text-gray-300 bg-gray-600">{status}</Badge>;
    }
  };

  const getDifficultyBadge = (level: string) => {
    const styles = {
      beginner: 'border-blue-700 text-blue-300 bg-blue-900/20',
      intermediate: 'border-amber-700 text-amber-300 bg-amber-900/20',
      advanced: 'border-rose-700 text-rose-300 bg-rose-900/20'
    } as const;

    const labels = {
      beginner: 'Débutant',
      intermediate: 'Intermédiaire',
      advanced: 'Avancé'
    } as const;

    const key = (['beginner','intermediate','advanced'] as const).includes(level as any)
      ? (level as 'beginner' | 'intermediate' | 'advanced')
      : 'beginner';

    return (
      <Badge variant="outline" className={`border ${styles[key]}`}>
        {labels[key]}
      </Badge>
    );
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'XAF',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Intl.DateTimeFormat('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    }).format(new Date(dateString));
  };

  if (authLoading || loading) {
    return (
      <InstructorLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#D4AF37]"></div>
        </div>
      </InstructorLayout>
    );
  }

  return (
    <InstructorLayout>
      <div className="space-y-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <motion.h1 
              className="text-3xl font-bold text-white"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              Mes Cours
            </motion.h1>
            <motion.p 
              className="text-gray-300 mt-1"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              Gérez vos cours, suivez les inscriptions et analysez les performances
            </motion.p>
          </div>
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <Button 
              onClick={() => navigate('/instructor/courses/new')}
              className="bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800 shadow-lg hover:shadow-xl transition-all duration-300 text-white"
            >
              <Plus className="h-4 w-4 mr-2" />
              Nouveau Cours
            </Button>
          </motion.div>
        </div>

        {/* Statistiques */}
        <motion.div 
          className="grid grid-cols-1 md:grid-cols-4 gap-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <Card className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 border-gray-700/30 shadow-lg hover:shadow-xl transition-shadow duration-300">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-white">Total Cours</CardTitle>
              <div className="p-2 rounded-lg bg-gradient-to-r from-blue-500 to-blue-600">
                <BookOpen className="h-5 w-5 text-white" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-white">
                {courses.length}
              </div>
              <p className="text-xs text-gray-300 mt-1">
                Cours créés
              </p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 border-gray-700/30 shadow-lg hover:shadow-xl transition-shadow duration-300">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-white">Étudiants</CardTitle>
              <div className="p-2 rounded-lg bg-gradient-to-r from-green-500 to-green-600">
                <Users className="h-5 w-5 text-white" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-white">
                {totalUniqueStudents}
              </div>
              <p className="text-xs text-gray-300 mt-1">
                Total étudiants
              </p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 border-gray-700/30 shadow-lg hover:shadow-xl transition-shadow duration-300">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-white">Note Moyenne</CardTitle>
              <div className="p-2 rounded-lg bg-gradient-to-r from-amber-500 to-amber-600">
                <Star className="h-5 w-5 text-white" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-white">
                {(() => {
                  const ratedCourses = courses.filter(c => (c.reviews_count ?? 0) > 0 && (c.average_rating ?? 0) > 0);
                  const avg = ratedCourses.length
                    ? ratedCourses.reduce((sum, c) => sum + (c.average_rating || 0), 0) / ratedCourses.length
                    : 0;
                  return avg.toFixed(1);
                })()}
              </div>
              <p className="text-xs text-gray-300 mt-1">
                Sur {courses.reduce((sum, course) => sum + (course.reviews_count || 0), 0)} avis
              </p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 border-gray-700/30 shadow-lg hover:shadow-xl transition-shadow duration-300">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-white">Revenus</CardTitle>
              <div className="p-2 rounded-lg bg-gradient-to-r from-purple-500 to-purple-600">
                <DollarSign className="h-5 w-5 text-white" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-white">
                {formatCurrency(
                  courses.reduce((sum, course) => sum + (course.price * course.enrollments_count || 0), 0)
                )}
              </div>
              <p className="text-xs text-gray-300 mt-1">
                Revenus totaux
              </p>
            </CardContent>
          </Card>
        </motion.div>

        {/* Filtres et recherche */}
        <motion.div 
          className="flex flex-col md:flex-row md:items-center gap-4 p-4 rounded-xl shadow-md bg-gradient-to-br from-gray-800/50 to-gray-900/50 border border-gray-700/30"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <Input
              placeholder="Rechercher un cours..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-gray-900/40 text-white placeholder-gray-400 border border-gray-700 focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:border-blue-500 transition-colors duration-300"
            />
          </div>
          
          <div className="flex gap-2 flex-wrap">
            <div className="relative">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[180px] bg-gray-900/40 border border-gray-700 text-gray-200 hover:bg-gray-800/40 focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:border-blue-500 transition-colors duration-300">
                  <Filter className="h-4 w-4 mr-2 text-gray-300" />
                  <span className="truncate">{STATUS_LABELS[statusFilter] ?? 'Statut'}</span>
                </SelectTrigger>
                <SelectContent className="bg-gray-800 border border-gray-700 text-gray-200">
                  <SelectItem value="all">Tous les statuts</SelectItem>
                  <SelectItem value="draft">Brouillon</SelectItem>
                  <SelectItem value="published">Publié</SelectItem>
                  <SelectItem value="archived">Archivé</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="relative">
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-[180px] bg-gray-900/40 border border-gray-700 text-gray-200 hover:bg-gray-800/40 focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:border-blue-500 transition-colors duration-300">
                  <SortAsc className="h-4 w-4 mr-2 text-gray-300" />
                  <span className="truncate">{SORT_LABELS[sortBy] ?? 'Trier par'}</span>
                </SelectTrigger>
                <SelectContent className="bg-gray-800 border border-gray-700 text-gray-200">
                  <SelectItem value="updated_at">Dernière modification</SelectItem>
                  <SelectItem value="created_at">Date de création</SelectItem>
                  <SelectItem value="title">Titre</SelectItem>
                  <SelectItem value="enrollments_count">Nombre d'inscriptions</SelectItem>
                  <SelectItem value="average_rating">Note moyenne</SelectItem>
                  <SelectItem value="price">Prix</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Valeurs actives (résumé) */}
          <div className="md:ml-auto flex items-center gap-3 text-sm text-gray-300 flex-wrap">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-gray-300" />
              <span>{sortedCourses.length} résultat{sortedCourses.length > 1 ? 's' : ''}</span>
            </div>
            {searchTerm && (
              <span className="px-2 py-0.5 rounded-full bg-gray-800/60 border border-gray-700">
                Recherche: <span className="text-gray-200">"{searchTerm}"</span>
              </span>
            )}
            <span className="px-2 py-0.5 rounded-full bg-gray-800/60 border border-gray-700">
              Statut: <span className="text-gray-200">{STATUS_LABELS[statusFilter] ?? statusFilter}</span>
            </span>
            <span className="px-2 py-0.5 rounded-full bg-gray-800/60 border border-gray-700">
              Tri: <span className="text-gray-200">{SORT_LABELS[sortBy] ?? sortBy}</span>
            </span>
          </div>
        </motion.div>

        {/* Liste des cours */}
        <motion.div 
          className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6"
          layout
        >
          {sortedCourses.map((course, index) => (
            <motion.div
              key={course.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
              whileHover={{ y: -5 }}
              className="h-full"
            >
              <Card className="relative overflow-hidden hover:shadow-xl transition-all duration-300 h-full flex flex-col bg-gradient-to-br from-gray-800/50 to-gray-900/50 border border-gray-700/30 rounded-2xl">
                {course.thumbnail_url ? (
                  <div className="h-48 overflow-hidden relative group">
                    <img 
                      src={course.thumbnail_url} 
                      alt={course.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    <div className="absolute bottom-4 left-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <Button 
                        variant="secondary" 
                        size="sm" 
                        className="bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800 text-white"
                        onClick={() => navigate(`/elearning/courses/${course.slug}`)}
                      >
                        <Eye className="h-4 w-4 mr-2" />
                        Aperçu
                      </Button>
                    </div>
                    <div className="absolute top-4 right-4">
                      {getStatusBadge(course.status)}
                    </div>
                  </div>
                ) : (
                  <div className="h-48 bg-gradient-to-r from-blue-600 to-indigo-700 flex items-center justify-center relative overflow-hidden">
                    <BookOpen className="h-16 w-16 text-white/80" />
                    <div className="absolute top-4 right-4">
                      {getStatusBadge(course.status)}
                    </div>
                  </div>
                )}
                
                <CardContent className="p-5 flex-grow flex flex-col">
                  <div className="flex justify-between items-start mb-3">
                    <h3 className="font-bold text-lg truncate text-white">{course.title}</h3>
                  </div>
                  
                  <p className="text-gray-300 text-sm mb-4 line-clamp-2 flex-grow">
                    {course.description || 'Aucune description disponible'}
                  </p>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {getDifficultyBadge(course.difficulty_level)}
                    <Badge variant="outline" className="flex items-center gap-1 border-gray-700 text-gray-300">
                      <Clock className="h-3 w-3" />
                      <span className="text-xs font-medium">{course.duration_hours}h</span>
                    </Badge>
                    <Badge variant="outline" className="flex items-center gap-1 border-gray-700 text-gray-300">
                      <Users className="h-3 w-3" />
                      <span className="text-xs font-medium">{course.enrollments_count}</span>
                    </Badge>
                    {course.reviews_count > 0 && (
                      <Badge variant="outline" className="flex items-center gap-1 border-gray-700 text-gray-300">
                        <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
                        <span className="text-xs font-medium">{course.average_rating.toFixed(1)}</span>
                      </Badge>
                    )}
                  </div>

                  <div className="flex justify-between items-center mb-4 pt-2 border-t border-gray-700/50">
                    <div className="text-xs text-gray-300 flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      {formatDate(course.updated_at)}
                    </div>
                    <div className="font-bold text-lg text-white">
                      {formatCurrency(course.price)}
                    </div>
                  </div>

                  <div className="flex gap-2 mt-auto">
                    <Button 
                      variant="default" 
                      size="sm" 
                      onClick={() => navigate(`/instructor/courses/${course.id}/edit`)}
                      className="flex-1 bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800 text-white shadow-md hover:shadow-lg transition-all duration-300"
                    >
                      <Edit className="h-4 w-4 mr-2" />
                      Modifier
                    </Button>
                    
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-gray-400 hover:text-white hover:bg-gray-700/50">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="shadow-lg rounded-xl border-gray-700 bg-gray-800">
                        <DropdownMenuLabel className="font-semibold text-white">Actions</DropdownMenuLabel>
                        <DropdownMenuSeparator className="bg-gray-700" />
                        <DropdownMenuItem onClick={() => navigate(`/elearning/courses/${course.slug}`)} className="cursor-pointer text-gray-300 hover:bg-gray-700/50 hover:text-white transition-colors duration-200">
                          <Eye className="mr-2 h-4 w-4 text-blue-400" />
                          Aperçu
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate(`/instructor/courses/${course.id}/edit`)} className="cursor-pointer text-gray-300 hover:bg-gray-700/50 hover:text-white transition-colors duration-200">
                          <Edit className="mr-2 h-4 w-4 text-blue-400" />
                          Modifier
                        </DropdownMenuItem>
                        <DropdownMenuSeparator className="bg-gray-700" />
                        {course.status === 'published' ? (
                          <DropdownMenuItem onClick={() => handleToggleStatus(course.id, 'draft')} className="cursor-pointer text-amber-400 hover:bg-amber-900/30 hover:text-amber-300 transition-colors duration-200">
                            <Pause className="mr-2 h-4 w-4" />
                            Mettre en brouillon
                          </DropdownMenuItem>
                        ) : (
                          <DropdownMenuItem onClick={() => handleToggleStatus(course.id, 'published')} className="cursor-pointer text-green-400 hover:bg-green-900/30 hover:text-green-300 transition-colors duration-200">
                            <Play className="mr-2 h-4 w-4" />
                            Publier
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuSeparator className="bg-gray-700" />
                        <DropdownMenuItem 
                          onClick={() => handleDeleteCourse(course.id)}
                          className="text-red-400 cursor-pointer hover:bg-red-900/30 hover:text-red-300 transition-colors duration-200"
                        >
                          <Trash className="mr-2 h-4 w-4" />
                          Supprimer
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </motion.div>

        {sortedCourses.length === 0 && (
          <motion.div 
            className="text-center py-12 rounded-2xl shadow-md p-8 bg-gradient-to-br from-gray-800/50 to-gray-900/50 border border-gray-700/30"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.6 }}
          >
            <BookOpen className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">
              {searchTerm || statusFilter !== 'all' ? 'Aucun cours trouvé' : 'Aucun cours créé'}
            </h3>
            <p className="text-gray-300 mb-6 max-w-md mx-auto">
              {searchTerm || statusFilter !== 'all' 
                ? 'Essayez de modifier vos critères de recherche.'
                : 'Commencez par créer votre premier cours et partagez vos connaissances avec le monde.'
              }
            </p>
            {!searchTerm && statusFilter === 'all' && (
              <Button 
                onClick={() => navigate('/instructor/courses/new')}
                className="bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800 text-white px-6 py-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <Plus className="h-5 w-5 mr-2" />
                Créer mon premier cours
              </Button>
            )}
          </motion.div>
        )}
      </div>
    </InstructorLayout>
  );
};

export default InstructorCoursesPage;
